<template>
  <div id="feedback-form">
    <div class="row">
      <div class="col-12 d-flex">
        <div class="profile-image-wrapper">
          <user-image v-if="user" :user="user" class="user-image" />
        </div>
        <textarea-autosize
          :placeholder="$t('feedback.modal.input.placeholder')"
          :class="{ 'reason-input': true, 'animate-shake': showReasonError }"
          ref="reason"
          :disabled="parentId"
          v-model="reason"
          :min-height="120"
          :max-height="150"
          maxlength="400"
          @blur.native="reasonChanged($event.target.value)"
        />
        <span class="character-count">{{ reasonCount }}/400</span>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div
          :class="{ 'categories-label': true, 'has-error': showCategoryError }"
        >
          {{ $t('feedback.modal.categories.label') }}
        </div>
      </div>
    </div>
    <div class="row">
      <div
        :class="{
          'col-12 category-col': true,
          'animate-shake': showCategoryError,
        }"
      >
        <div
          v-for="(coreValue, index) in coreValues"
          :key="index"
          :class="{
            category: true,
            selected: selectedCategoryIndex == coreValue.index,
            disabled: parentId,
          }"
          @click="selectCategory(coreValue.index)"
        >
          <div class="category-icon" v-html="coreValue.emoji"></div>
          <span class="category-name">{{ coreValue.name }}</span>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div :class="{ 'amounts-label': true, 'has-error': showAmountError }">
          {{ $t('feedback.modal.amount.label') }}
        </div>
      </div>
    </div>
    <div class="row">
      <div
        :class="{ 'col-6 amounts-col': true, 'animate-shake': showAmountError }"
      >
        <div
          v-for="(amount, index) in amounts"
          :key="index"
          @click="selectAmount(index)"
          :class="{ amount: true, selected: selectedIndex == index }"
        >
          <span>{{ amount.value }}</span>
        </div>
      </div>
      <div class="col-6">
        <base-button
          @click="feedback"
          :text="buttonText"
          :class="buttonClass"
        />
      </div>
    </div>
  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex';
import BaseInput from '@/components/shared/elements/BaseInput';
import BaseButton from '@/components/shared/elements/BaseButton';
import UserImage from '@/components/shared/users/UserImage';
import CategoryIcons from '@/components/shared/categoryIcons';

export default {
  name: 'FeedbackForm',
  components: {
    BaseInput,
    BaseButton,
    UserImage,
  },
  mixins: [CategoryIcons],
  props: {
    receiverUserId: {
      required: true,
      type: String,
    },
    initialReason: {
      type: String,
      required: false,
    },
    initialAmount: {
      type: Number,
      required: false,
    },
    initialCategory: {
      type: Number,
      required: false,
    },
    parentId: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      reason: undefined,
      showReasonError: false,
      showCategoryError: false,
      showAmountError: false,
      amounts: [
        {
          value: 1,
        },
        {
          value: 3,
        },
        {
          value: 5,
        },
      ],
      selectedIndex: undefined,
      selectedCategoryIndex: undefined,
      user: undefined,
      buttonNeedConfirm: false,
      isLoading: false,
    };
  },
  async created() {
    this.user = await this.fetchUser(this.receiverUserId);
    if (this.initialReason) {
      this.reason = this.initialReason;
    }
    if (this.initialAmount) {
      const selectedAmount = this.amounts.filter(
        (x) => x.value === this.initialAmount
      )[0];
      const possibleIndex = this.amounts.indexOf(selectedAmount);
      this.selectedIndex = [1, 3, 5].includes(possibleIndex)
        ? possibleIndex
        : undefined;
    }
    console.log(this.initialCategory);
    if (this.initialCategory !== undefined) {
      this.selectedCategoryIndex = this.initialCategory;
    }
  },
  computed: {
    ...mapGetters({
      loggedInUser: 'users/loggedInUserProfile',
    }),
    coreValues() {
      return this.loggedInUser.company.coreValues;
    },
    buttonText() {
      if (this.buttonNeedConfirm) {
        return this.$t('feedback.modal.button.needConfirmText');
      }
      return this.$t('feedback.modal.button.text');
    },
    buttonClass() {
      if (this.buttonNeedConfirm) {
        return 'confirm-button pulse animated';
      }
      return '';
    },
    reasonCount() {
      if (this.reason) {
        return this.reason.length;
      }
      return 0;
    },
  },
  methods: {
    ...mapActions({
      sendFeedback: 'transactions/sendFeedback',
      getCurrentLeaderboard: 'leaderboard/getCurrentLeaderboard',
      getTransactions: 'leaderboard/getTransactions',
      fetchLoggedInProfile: 'users/fetchLoggedInProfile',
      fetchUser: 'users/fetchUser',
    }),
    reasonChanged(reason) {
      if (reason.length > 3) {
        this.showReasonError = false;
      }
      this.reason = reason;
    },
    selectAmount(index) {
      this.selectedIndex = index;
    },
    selectCategory(index) {
      if (!this.parentId) {
        this.selectedCategoryIndex = index;
      }
    },
    feedback() {
      if (this.isLoading) {
        return;
      }
      if (!this.reason || this.reason.length < 4) {
        this.showReasonError = true;
        setTimeout(() => {
          this.showReasonError = false;
        }, 500);
        return;
      }
      if (this.selectedCategoryIndex == null) {
        this.showCategoryError = true;
        setTimeout(() => {
          this.showCategoryError = false;
        }, 500);
        return;
      }
      if (this.selectedIndex == null) {
        this.showAmountError = true;
        setTimeout(() => {
          this.showAmountError = false;
        }, 500);
        return;
      }
      if (!this.buttonNeedConfirm) {
        this.buttonNeedConfirm = true;
        return;
      }
      this.isLoading = true;
      this.sendFeedback({
        amount: this.amounts[this.selectedIndex].value,
        reason: this.reason,
        receiverUserId: this.receiverUserId,
        category: this.selectedCategoryIndex,
        parentId: this.parentId,
      })
        .then((x) => {
          this.getTransactions();
          this.getCurrentLeaderboard();
          this.fetchLoggedInProfile();

          if (x._id) {
            // eslint-disable-line
            const sTitle = this.$t('feedback.success.title');
            const sMessage = this.$t('feedback.success.message');
            this.$swal(sTitle, sMessage, 'success');
          } else {
            const eTitle = this.$t('feedback.error.title');
            const eMessage = this.$t('feedback.error.message');
            this.$swal(eTitle, eMessage, 'error');
          }
          this.$gtag.event('feedback', {
            category: this.selectedCategoryIndex,
            amount: this.amounts[this.selectedIndex].value,
          });
          this.isLoading = false;
          this.$eventBus.closeModal();
        })
        .catch((err) => {
          const eTitle = this.$t('feedback.error.title');
          this.$swal(eTitle, err.message, 'error');
          this.isLoading = false;
          this.$eventBus.closeModal();
        });
    },
    cancel() {
      this.$eventBus.closeModal();
    },
  },
};
</script>

<style lang="scss" scoped>
#feedback-form {
  min-width: 450px;
  text-align: left;
  padding: 0 !important;
  @media (min-width: 992px) {
    min-width: 650px;
  }

  .cancel {
    cursor: pointer;
    &:hover {
      color: $secondary-color;
    }
  }
  .profile-image-wrapper {
    width: 85px;
    height: 85px;
  }
  .reason-input {
    border: none;
    margin-left: 15px;
    flex: 1 33.33%;
    outline: none;
    max-height: 120px;
    background-color: rgba(53, 54, 61, 0.035);
    padding: 15px;
    border-radius: 8px;
  }
  .categories-label,
  .amounts-label {
    font-size: 13px;
    font-weight: bold;
    opacity: 0.35;
    -webkit-transition: all 300ms ease-out;
    -moz-transition: all 300ms ease-out;
    -o-transition: all 300ms ease-out;
    -ms-transition: all 300ms ease-out;
    transition: all 300ms ease-out;
  }
  textarea::-webkit-input-placeholder {
    opacity: 0.45;
  }

  textarea:-moz-placeholder {
    /* Firefox 18- */
    opacity: 0.45;
  }

  textarea::-moz-placeholder {
    /* Firefox 19+ */
    opacity: 0.45;
  }

  textarea:-ms-input-placeholder {
    opacity: 0.45;
  }

  textarea::placeholder {
    opacity: 0.45;
  }
  .category-col {
    display: flex;
    .category {
      font-size: 12px;
      display: flex;
      margin: 2.5px;
      padding: 5px;
      border: 1px solid rgb(245, 246, 247);
      background-color: rgb(245, 246, 247);
      border-radius: 8px;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      -webkit-transition: all 350ms ease-out;
      -moz-transition: all 350ms ease-out;
      -o-transition: all 350ms ease-out;
      -ms-transition: all 350ms ease-out;
      transition: all 350ms ease-out;
      width: 58px;
      height: 38px;
      overflow: hidden;
      position: relative;
      flex: 1;
      .category-icon {
        text-align: center;
        font-size: 18px;
        position: absolute;
        width: 30px;
        left: 50%;
        top: 50%;
        -webkit-transform: translate(-50%, -50%);
        -ms-transform: translate(-50%, -50%);
        transform: translate(-50%, -50%);
      }
      &.disabled {
        opacity: 0.45;
      }
      &.selected {
        opacity: 1;
        border: 1px solid #4b56a9;
        .category-name {
          opacity: 1;
          color: #4b56a9;
        }
      }
      &:hover {
        width: 150px;
        flex: 2.5;
        -webkit-transition: all 450ms ease-out;
        -moz-transition: all 450ms ease-out;
        -o-transition: all 450ms ease-out;
        -ms-transition: all 450ms ease-out;
        transition: all 450ms ease-out;
        .category-icon {
          left: 25px;
          -webkit-transition: all 300ms ease-out;
          -moz-transition: all 300ms ease-out;
          -o-transition: all 300ms ease-out;
          -ms-transition: all 300ms ease-out;
          transition: all 300ms ease-out;
        }
        .category-name {
          left: 45px;
          visibility: visible;
          -webkit-transition: all 350ms ease-out;
          -moz-transition: all 350ms ease-out;
          -o-transition: all 350ms ease-out;
          -ms-transition: all 350ms ease-out;
          transition: all 350ms ease-out;
        }
      }
      .category-name {
        position: absolute;
        left: 80px;
        top: 50%;
        width: 100px;
        -webkit-transform: translateY(-50%);
        -ms-transform: translateY(-50%);
        opacity: 0.35;
        transform: translateY(-50%);
        visibility: hidden;
        -webkit-transition: all 300ms ease-out;
        -moz-transition: all 300ms ease-out;
        -o-transition: all 300ms ease-out;
        -ms-transition: all 300ms ease-out;
        transition: all 300ms ease-out;
      }
    }
  }
  .amounts-col {
    display: flex;
    .amount {
      background-color: rgb(245, 246, 247);
      border: 1px solid rgb(245, 246, 247);
      width: 60px;
      height: 40px;
      border-radius: 8px;
      margin-right: 9px;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      span {
        opacity: 0.35;
      }
      &.selected {
        border: 1px solid #4b56a9;
        span {
          opacity: 1;
          color: #4b56a9;
        }
      }
    }
  }
  .close-icon {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 1;
    font-size: 16px;
    color: #aaa;
    cursor: pointer;
    &:hover {
      color: $base-font-color;
    }
  }
  .has-error {
    color: #e32a2a;
    opacity: 1;
    -webkit-transition: all 300ms ease-out;
    -moz-transition: all 300ms ease-out;
    -o-transition: all 300ms ease-out;
    -ms-transition: all 300ms ease-out;
    transition: all 300ms ease-out;
  }
  .confirm-button {
    >>> .base-button {
      background-color: $secondary-color;
    }
  }
  .character-count {
    position: absolute;
    bottom: 10px;
    right: 20px;
    font-size: 12px;
    color: #aaa;
    font-weight: bold;
  }
}
</style>

